import { Button, Heading, Img, Text, TextArea } from 'components'
import React, { useCallback, useMemo, useState } from 'react'
import { default as ModalProvider } from 'react-modal'

const TableListMaturityPool = ({ data, onUpdate }) => {
  const [sortDir, setSortDir] = useState('asc')
  const [sortCol, setSortCol] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [action, setAction] = useState('')
  const [reason, setReason] = useState()
  const [selectedItem, setSelectedItem] = useState()

  const _onClickYes = useCallback(async () => {
    setIsOpen(false)
    onUpdate && onUpdate({
      ...selectedItem,
      status: action === 'Approve' ? 'approved' : 'rejected',
      active_date: selectedItem.active_date.substr(0, 10),
      active_until: selectedItem.active_until.substr(0, 10),
      reason
    })
  }, [selectedItem, action, reason])

  const _handleSort = useCallback((col) => {
    if (col === sortCol) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(col)
      setSortDir('desc')
    }
  }, [sortCol, sortDir])

  const _headCol = useCallback((content, className, _sortCol) => {
    return (
      <div className={`${className} flex items-center gap-1`}>
        <Text as="p" className="!font-bold !text-blue_gray-200 tracking-[-0.14px] ">
          {content}
        </Text>
        {
          _sortCol ?
            <Img
              onClick={() => _handleSort(_sortCol)}
              src='images/img_arrow_down.svg'
              className={`${sortDir === 'asc' && sortCol === _sortCol && '!rotate-180'}`}
            />
            : null
        }
      </div>
    )
  }, [sortDir, sortCol, setSortCol, setSortDir])

  const _col = useCallback((content, className = 'w-[15%]') => {
    return (
      <div className={`${className} flex`}>
        <Text as="p" className="!text-blue_gray-900_06 tracking-[-0.14px]">
          {content}
        </Text>
      </div>
    )
  }, [])

  const _fileCol = useCallback((content) => {
    return (
      <div className='w-[20%] flex'>
        <Text as="a" href={content} target="_blank" className="!text-blue-500 underline tracking-[-0.14px]">
          View Document
        </Text>
      </div>
    )
  }, [])

  const _actionCol = useCallback((item) => {
    if (item.status === 'pending') {
      return (
        <div className='w-[15%] flex'>
          <Img
            onClick={() => {
              setSelectedItem(item)
              setAction('Approve')
              setIsOpen(true)
            }}
            src="images/img_ic_baseline_check.svg"
            alt="materialsymbol"
            className="h-[24px] w-[24px] ml-2.5"
          />
          <Img
            onClick={() => {
              setSelectedItem(item)
              setAction('Reject')
              setIsOpen(true)
            }}
            src="images/img_ic_outline_close.svg"
            alt="materialsymbol"
            className="h-[24px] w-[24px] ml-2.5"
          />
        </div>
      )
    }
  }, [])

  const _row = useCallback((item, index) => {
    const {
      id,
      active_date,
      active_until,
      status,
      pool_agreement
    } = item

    return (
      <div key={index} className="flex items-center w-full hover:bg-gray-50_02 py-5 gap-1">
        {_col(id, 'w-[20%]')}
        {_col(active_date.substr(0, 10))}
        {_col(active_until.substr(0, 10))}
        {_col(status.charAt(0).toUpperCase() + status.substr(1))}
        {_fileCol(pool_agreement)}
        {_actionCol(item)}
      </div>
    )
  }, [_col, _fileCol, _actionCol])

  const _sortedData = useMemo(() => {
    if (sortCol) {
      return [...data].sort((a, b) => {
        let aValue = a[sortCol]
        let bValue = b[sortCol]
        if (aValue < bValue) {
          return sortDir === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDir === 'asc' ? 1 : -1;
        }
        return 0
      })
    }

    return data
  }, [data, sortDir, sortCol])

  return (
    <>
      <div className='w-full p-10 flex flex-col gap-10'>
        <div className="flex flex-col w-full">
          <div className="flex items-center w-full pb-5">
            {_headCol('ID', 'w-[20%]')}
            {_headCol('Active Date', 'w-[15%]', 'active_date')}
            {_headCol('Active Until', 'w-[15%]', 'active_until')}
            {_headCol('Status', 'w-[15%]', 'status')}
            {_headCol('Agreement Doc', 'w-[20%]')}
            {_headCol('Action', 'w-[15%]')}
          </div>
          <div className='h-px bg-orange-100 w-full'></div>
          <div className="flex flex-col items-center justify-start w-full pb-10">
            {_sortedData.map(_row)}
          </div>
        </div>
      </div>
      <ModalProvider
        style={{
          overlay: {
            backgroundColor: isOpen ? "#D9D9D9CC" : "transparent",
          },
          content: {
            outline: "none",
          },
        }}
        appElement={document.getElementById("root")} isOpen={isOpen} className="w-[30%]">
        <div className="flex flex-col items-start justify-start w-full p-10 bg-white-A700 rounded-[10px]">
          <Heading size="lg" as="h1" className='!font-poppins !font-semibold text-black-900'>
            {action} Confirm
          </Heading>
          {action === 'Reject' &&
            <TextArea
              onChange={(e) => { setReason(e); }}
              name="input_one"
              placeholder={`Enter your rejection reason`}
              className="w-full text-blue_gray-500 mt-5"
            />
          }
          <div className="flex justify-between w-full mt-10">
            <Button
              onClick={_onClickYes}
              size="sm"
              color="white_A700"
              className="tracking-[0.40px] font-poppins uppercase font-semibold w-fit rounded-[10px] !px-10 !py-6 !shadow-[0_10px_15px_0px_rgba(0,0,0,0.2)] "
            >
              {action}
            </Button>
            <Button
              onClick={() => { setIsOpen(false) }}
              size="sm"
              color="blue_900"
              className="tracking-[0.40px] font-poppins uppercase font-semibold w-fit rounded-[10px] !px-10 !py-6 !shadow-[0_10px_15px_0px_rgba(0,0,0,0.2)] "
            >
              Cancel
            </Button>
          </div>
        </div>
      </ModalProvider>
    </>
  )
}

export default React.memo(TableListMaturityPool)